import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress
} from '@mui/material';
import {
  Timeline as StoryPointIcon,
  Save as SaveIcon
} from '@mui/icons-material';
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const StoryPointEstimateMenu = ({ 
  issue, 
  onStoryPointChange, 
  onClose 
}) => {
  const [storyPoints, setStoryPoints] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Initialize with current story points
  useEffect(() => {
    if (issue.storyPoints !== null && issue.storyPoints !== undefined) {
      setStoryPoints(issue.storyPoints.toString());
    }
  }, [issue.storyPoints]);

  const handleStoryPointUpdate = async () => {
    setError('');
    
    // Validate input
    const value = storyPoints.trim();
    let parsedValue = null;
    
    if (value !== '') {
      parsedValue = parseFloat(value);
      if (isNaN(parsedValue) || parsedValue < 0) {
        setError('Please enter a valid positive number');
        return;
      }
    }

    setIsLoading(true);
    try {
       const token = localStorage.getItem('token');
    if (!token) {
      alert("No authentication token found. Please log in.");
      return;
    }
  
    const payload = JSON.parse(atob(token.split('.')[1]));
    const company_id = payload.company_id;
      const response = await fetch(`${API_URL}/api/issues/${issue.id}/story-points`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          storyPoints: parsedValue
        })
      });

      const data = await response.json();
      
      if (data.status === 'success') {
        if (onStoryPointChange) {
          onStoryPointChange(issue.id, parsedValue);
        }
        if (onClose) {
          onClose();
        }
      } else {
        setError(data.message || 'Failed to update story points');
      }
    } catch (error) {
      //console.error('Error updating story points:', error);
      setError('Error updating story points');
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleStoryPointUpdate();
    }
  };

  const handleClear = () => {
    setStoryPoints('');
    setError('');
  };

  return (
    <Box
      sx={{
        minWidth: '320px',
        maxWidth: '380px',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      {/* Header */}
      <Box sx={{ padding: '16px 20px 12px', borderBottom: '1px solid #f0f0f0' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <StoryPointIcon sx={{ fontSize: '18px', mr: 1, color: 'text.secondary' }} />
          <Typography variant="subtitle2" fontWeight="600" color="text.primary">
            Story point estimate
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
          Enter the effort estimate for this work item
        </Typography>
      </Box>

      {/* Input Section */}
      <Box sx={{ padding: '20px' }}>
        <TextField
          fullWidth
          size="small"
          label="Story Points"
          placeholder="Enter story points (e.g., 3, 5, 8)"
          value={storyPoints}
          onChange={(e) => {
            setStoryPoints(e.target.value);
            setError('');
          }}
          onKeyPress={handleKeyPress}
          error={!!error}
          helperText={error || 'Leave empty for no estimate'}
          type="number"
          inputProps={{
            min: 0,
            step: 0.5
          }}
          sx={{
            mb: 2,
            '& .MuiOutlinedInput-root': {
              fontSize: '0.875rem'
            }
          }}
          disabled={isLoading}
        />

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
          <Button
            variant="outlined"
            size="small"
            onClick={handleClear}
            disabled={isLoading || !storyPoints}
            sx={{ fontSize: '0.75rem' }}
          >
            Clear
          </Button>
          <Button
            variant="contained"
            size="small"
            onClick={handleStoryPointUpdate}
            disabled={isLoading}
            startIcon={isLoading ? <CircularProgress size={16} /> : <SaveIcon sx={{ fontSize: '16px' }} />}
            sx={{ fontSize: '0.75rem' }}
          >
            {isLoading ? 'Saving...' : 'Save'}
          </Button>
        </Box>
      </Box>
      
      {/* Footer */}
      
    </Box>
  );
};

export default StoryPointEstimateMenu;